export const AEO_MARKDOWN_ENTRY_ATTRIBUTE =
  'data-aeo-markdown-entry-id' as const;

export const AEO_MARKDOWN_CONTENT_TYPE = 'aeoMarkdown' as const;

export type AeoMarkdownBodies = Readonly<Record<string, string>>;

export type AeoMarkdownEntries = Readonly<
  Record<string, Readonly<Record<string, string>>>
>;

export type AeoMarkdownExport = {
  entries: AeoMarkdownEntries;
};

type AeoMarkdownEntry = {
  sys?: { id?: unknown };
  fields?: { body?: unknown };
};

const DEFAULT_LOCALE = 'en-US';
const LOCALIZED_PATH_LOCALES = new Map([
  ['en-gb', 'en-GB'],
  ['en-eu', 'en-EU'],
]);

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const isAeoMarkdownEntry = (value: Record<string, unknown>): boolean => {
  return (
    isRecord(value.sys) &&
    isRecord(value.sys.contentType) &&
    isRecord(value.sys.contentType.sys) &&
    value.sys.contentType.sys.id === AEO_MARKDOWN_CONTENT_TYPE
  );
};

/**
 * Remove AEO-only source bodies before CMS data becomes browser-visible page
 * props. The entry ID remains so the renderer can emit a marker for the
 * server-side markdown resolver.
 *
 * Mutates in place; tracks visited objects to handle cycles and shared
 * references. `normalizeAssetUrls` walks these same responses the same way.
 */
export const stripAeoMarkdownBodies = <T>(
  value: T,
  seen: WeakSet<object> = new WeakSet(),
): T => {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (seen.has(value)) {
    return value;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item) => {
      stripAeoMarkdownBodies(item, seen);
    });
    return value;
  }

  const record = value as Record<string, unknown>;
  if (isAeoMarkdownEntry(record) && isRecord(record.fields)) {
    delete record.fields.body;
  }

  Object.values(record).forEach((child) => {
    stripAeoMarkdownBodies(child, seen);
  });

  return value;
};

export const getAeoMarkdownLocaleForFilePath = (filePath: string): string => {
  const firstSegment = filePath
    .replace(/^\/?out\/?/, '')
    .split('/')
    .filter(Boolean)[0]
    ?.toLowerCase();

  return LOCALIZED_PATH_LOCALES.get(firstSegment || '') || DEFAULT_LOCALE;
};

export const buildAeoMarkdownBodies = (
  data: unknown,
  locale = DEFAULT_LOCALE,
): AeoMarkdownBodies => {
  if (!data || typeof data !== 'object' || !('entries' in data)) {
    return {};
  }

  const entries = data.entries;
  if (!entries || typeof entries !== 'object' || Array.isArray(entries)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(entries).flatMap(([id, localizedBodies]) => {
      if (
        !localizedBodies ||
        typeof localizedBodies !== 'object' ||
        Array.isArray(localizedBodies)
      ) {
        return [];
      }

      const bodies = localizedBodies as Record<string, unknown>;
      const body = bodies[locale] ?? bodies[DEFAULT_LOCALE];
      return typeof body === 'string' ? [[id, body]] : [];
    }),
  );
};

export const serializeAeoMarkdownEntries = (
  items: readonly AeoMarkdownEntry[],
): AeoMarkdownExport => {
  const entries: Record<string, Record<string, string>> = {};

  items.forEach((entry) => {
    const id = entry?.sys?.id;
    const body = entry?.fields?.body;
    if (
      typeof id !== 'string' ||
      !body ||
      typeof body !== 'object' ||
      Array.isArray(body)
    ) {
      return;
    }

    const localizedBodies = Object.fromEntries(
      Object.entries(body).filter((value): value is [string, string] => {
        return typeof value[1] === 'string';
      }),
    );
    if (Object.keys(localizedBodies).length > 0) {
      entries[id] = localizedBodies;
    }
  });

  return { entries };
};

export const extractAeoMarkdownEntryIds = (html: string): string[] => {
  const ids = new Set<string>();
  const attributePattern = new RegExp(
    `${AEO_MARKDOWN_ENTRY_ATTRIBUTE}\\s*=\\s*["']([^"']+)["']`,
    'gi',
  );

  for (const match of html.matchAll(attributePattern)) {
    ids.add(match[1]);
  }

  return [...ids];
};
